/* lib/api/crm.js — клиент внутренней CRM (воронки продаж).
 * Раздел НЕ привязан к пространству — X-Workspace-Id бэком игнорируется.
 * Все функции unwrap'ят {ok,status,data} → бросают err{status,code,detail}
 * по контракту fakeApi, чтобы useFakeQuery и catch-ветки мутаций работали. */

import { apiFetch } from './client.js';
import { unwrap } from './unwrap.js';

// ── Мета (справочники + статус приёма заявок) ─────────────────────────────
export const getCrmMeta = () => unwrap(apiFetch('/api/crm/meta/'));
// Сотрудники с доступом к CRM — для назначения ответственного и фильтра.
export const listStaff = () => unwrap(apiFetch('/api/crm/staff/'));

// ── Воронки ───────────────────────────────────────────────────────────────
export const listPipelines = () => unwrap(apiFetch('/api/crm/pipelines/'));
export const createPipeline = (body) => unwrap(apiFetch('/api/crm/pipelines/', { method: 'POST', body }));
export const updatePipeline = (id, body) => unwrap(apiFetch(`/api/crm/pipelines/${id}/`, { method: 'PATCH', body }));
export const archivePipeline = (id) => unwrap(apiFetch(`/api/crm/pipelines/${id}/`, { method: 'DELETE' }));
export function getBoard(pipelineId, params = {}) {
  const q = new URLSearchParams();
  for (const k of ['q', 'source', 'owner']) {
    if (params[k] != null && params[k] !== '') q.set(k, String(params[k]));
  }
  const qs = q.toString();
  return unwrap(apiFetch(`/api/crm/pipelines/${pipelineId}/board/${qs ? `?${qs}` : ''}`));
}
export const getPipelineMetrics = (id) => unwrap(apiFetch(`/api/crm/pipelines/${id}/metrics/`));

// ── Этапы (конструктор воронки) ─────────────────────────────────────────────
export const addStage = (pipelineId, body) => unwrap(apiFetch(`/api/crm/pipelines/${pipelineId}/stages/`, { method: 'POST', body }));
export const updateStage = (id, body) => unwrap(apiFetch(`/api/crm/stages/${id}/`, { method: 'PATCH', body }));
export const archiveStage = (id) => unwrap(apiFetch(`/api/crm/stages/${id}/`, { method: 'DELETE' }));
export const reorderStages = (pipelineId, order) => unwrap(apiFetch(`/api/crm/pipelines/${pipelineId}/stages/reorder/`, { method: 'POST', body: { order } }));

// ── Лиды ────────────────────────────────────────────────────────────────────
export function listLeads(params = {}) {
  const q = new URLSearchParams();
  for (const k of ['pipeline', 'stage', 'owner', 'source', 'q', 'limit', 'offset', 'archived']) {
    if (params[k] != null && params[k] !== '') q.set(k, String(params[k]));
  }
  const qs = q.toString();
  return unwrap(apiFetch(`/api/crm/leads/${qs ? `?${qs}` : ''}`));
}
export const getLead = (id) => unwrap(apiFetch(`/api/crm/leads/${id}/`));
export const createLead = (body) => unwrap(apiFetch('/api/crm/leads/', { method: 'POST', body }));
export const updateLead = (id, body) => unwrap(apiFetch(`/api/crm/leads/${id}/`, { method: 'PATCH', body }));
export const archiveLead = (id) => unwrap(apiFetch(`/api/crm/leads/${id}/`, { method: 'DELETE' }));
export const restoreLead = (id) => unwrap(apiFetch(`/api/crm/leads/${id}/restore/`, { method: 'POST' }));
// Массовое действие: { ids, action: 'archive'|'reassign'|'move', owner?, to_stage?, reason? }
export const batchUpdateLeads = (body) => unwrap(apiFetch('/api/crm/leads/batch/', { method: 'PATCH', body }));

// Перемещение лида: причина (reason) ОБЯЗАТЕЛЬНА — бэк отклонит пустую (400 reason_required).
export const moveLead = (id, toStage, reason) =>
  unwrap(apiFetch(`/api/crm/leads/${id}/move/`, { method: 'POST', body: { to_stage: toStage, reason } }));

// Перенос/копирование лида в другую воронку. copy=true → лид остаётся и в исходной
// («и то, и то»), создаётся новый лид в целевой; иначе лид переезжает целиком.
export const transferLead = (id, { to_pipeline, to_stage, reason, copy }) =>
  unwrap(apiFetch(`/api/crm/leads/${id}/transfer/`, { method: 'POST',
    body: { to_pipeline, to_stage: to_stage || null, reason, copy: !!copy } }));

// ── Заметки (append-only) и история перемещений ───────────────────────────
export const listNotes = (id) => unwrap(apiFetch(`/api/crm/leads/${id}/notes/`));
export const addNote = (id, body) => unwrap(apiFetch(`/api/crm/leads/${id}/notes/`, { method: 'POST', body: { body } }));
export const listTransitions = (id) => unwrap(apiFetch(`/api/crm/leads/${id}/transitions/`));
